import { ReplaySubject } from 'rxjs';
import { ListStatusCount } from './../model/list-status-count.model';
import {Injectable} from '@angular/core';
import { CplRequestItem } from '../model/cpl-request-item.model';
import { UserRequestApi } from '../api/user-request.api';
import { CplUserRequest } from '../model/cpl-user-request.model';
import { CplStartUserRequest } from '../model/cpl-start-user-request.model';
import { CplChangeUserRequestManager } from '../model/cpl-change-user-request-manager.modal';
import { finalize, tap } from 'rxjs/operators';
import { PagedResponse, SessionService } from '@edl-development/edl-common';
import { TimeTrackingDetail } from '../model/cpl-time-tracking.model';
import { UserTimeTrackingSubmission } from '../time-tracking/model/user-time-tracking-form/user-time-tracking-submission.model';
import { RequestTypeFilter } from '../all-users-progress/common/enums/request-type-filter.enum';
import { UserArchivedFilter } from '../enums/user-request/user-archived-filter.enum';

@Injectable()
export class UserRequestService {

    private _listStatusCount: ListStatusCount = null;
    listStatusCount:ReplaySubject<any> = new ReplaySubject(1);

    constructor(
        private userRequestApi: UserRequestApi, 
        private sessionService:SessionService
    ) {
        this._listStatusCount = new ListStatusCount();
    }

    BroadcastListStatusCount(pagedResponse:PagedResponse){
        let statusCount = new ListStatusCount();
        statusCount.TotalPending = pagedResponse.TotalPending;
        statusCount.TotalExpiring = pagedResponse.TotalExpiring;
        statusCount.TotalExpired = pagedResponse.TotalExpired;
        this._listStatusCount = statusCount;
        this.listStatusCount.next(this._listStatusCount);
    }

    startUserRequest = (requestItem: CplRequestItem) => this.userRequestApi.startUserRequest(requestItem);

    startUserRequestForOtherUser = (data: CplStartUserRequest) => this.userRequestApi.startUserRequestForOtherUser(data);

    changeUserRequestManager = (data: CplChangeUserRequestManager) => this.userRequestApi.changeUserRequestManager(data);

    getUserRequest = (userRequestID: string) => this.userRequestApi.getUserRequest(userRequestID);

    getUserRequestsForItem = ({
        requestId,
        offset,
        limit,
        status,
        orderBy,
        ascending,
        searchBy,
        act48Status,
        approverUserId,
        groupIds,
        requestType,
        targetUserId,
        userArchivedFilter
    } : {
        requestId: string,
        offset: number,
        limit: number,
        status: number,
        orderBy?: string,
        ascending?: boolean,
        searchBy?: string,
        act48Status?: number,
        approverUserId?: string,
        groupIds?: string[],
        requestType?: RequestTypeFilter,
        targetUserId?: string,
        userArchivedFilter?: UserArchivedFilter
    }) => this.userRequestApi.getUserRequestsForItem({ requestId, offset, limit, status, orderBy, ascending, searchBy, act48Status, approverUserId, groupIds, requestType, targetUserId, userArchivedFilter });

    getUserRequestsForItemBasicUser = ({
        requestId,
        offset,
        limit,
        status,
        orderBy,
        ascending,
        searchBy,
        act48Status,
        approverUserId,
        groupIds,
        requestType,
        userArchivedFilter
    } : {
        requestId: string,
        offset: number,
        limit: number,
        status: number,
        orderBy?: string,
        ascending?: boolean,
        searchBy?: string,
        act48Status?: number,
        approverUserId?: string,
        groupIds?: string[],
        requestType?: RequestTypeFilter,
        userArchivedFilter?: UserArchivedFilter
    }) => this.userRequestApi.getUserRequestsForItemBasicUser({ requestId, offset, limit, status, orderBy, ascending, searchBy, act48Status, approverUserId, groupIds, requestType, userArchivedFilter });

    getUserRequestHistory(
        requestID: string,
        offset: number,
        limit: number,
        orderBy: string,
        ascending: boolean,
        searchBy?: string,
        status?: number,
        tag?:number,
        updateStatusCounts:boolean=true
    ) {
        this.sessionService.AppLoading(true);
        return this.userRequestApi
            .getUserRequestHistory(requestID, offset, limit, orderBy, ascending, searchBy, status, tag)
            .pipe(
                tap((data: PagedResponse) => {
                    if(updateStatusCounts){
                        this.BroadcastListStatusCount(data);
                    }
                }),
                finalize(() => this.sessionService.AppLoading(false))
            );
    }

    getMyRequests(
        offset: number,
        limit: number,
        orderBy: string,
        ascending: boolean,
        searchBy?: string,
        status?: number,
        tag?:number,
        updateStatusCounts:boolean=true,
        requestType?: RequestTypeFilter,
        includeUserStatusCount: boolean = false
    ) {
        this.sessionService.AppLoading(true);
        return this.userRequestApi
            .getMyRequests(offset, limit, orderBy, ascending, searchBy, status, tag, requestType, includeUserStatusCount)
            .pipe(
                tap((data: PagedResponse) => {
                    if(updateStatusCounts){
                        this.BroadcastListStatusCount(data);
                    }
                }),
                finalize(() => this.sessionService.AppLoading(false))
            );
    }

    getRequestsForUser(
        targetUserId: string,
        offset: number,
        limit: number,
        orderBy: string,
        ascending: boolean,
        searchBy?: string,
        status?: number,
        tag?: number,
        requestType?: RequestTypeFilter,
        updateStatusCounts:boolean=true,
        includeUserStatusCount: boolean = false
    ) {
        this.sessionService.AppLoading(true);
        return this.userRequestApi
            .getRequestsForUser(targetUserId, offset, limit, orderBy, ascending, searchBy, status, tag, requestType, includeUserStatusCount)
            .pipe(
                tap((data: PagedResponse) => {
                    if(updateStatusCounts){
                        this.BroadcastListStatusCount(data);
                    }
                }),
                finalize(() => this.sessionService.AppLoading(false))
            );
    }

    submitForVerification = (userRequest: CplUserRequest) => this.userRequestApi.submitForVerification(userRequest);

    archiveUserRequestAsTargetUser = (userRequest: CplUserRequest) => this.userRequestApi.archiveRequestAsTargetUser(userRequest);

    archiveUserRequestAsSupervisor = (userRequestId: string) => this.userRequestApi.archiveRequestAsSupervisor(userRequestId);

    bulkArchiveUserRequestAsSupervisor = (userRequestIds: string[]) => this.userRequestApi.bulkArchiveRequestAsSupervisor(userRequestIds);

    activateUserRequestAsTargetUser = (userRequest: CplUserRequest) => this.userRequestApi.activateRequestAsTargetUser(userRequest);

    activateUserRequestAsSupervisor = (userRequest: CplUserRequest) => this.userRequestApi.activateRequestAsSupervisor(userRequest);

    cancelUserRequest = (userRequest: CplUserRequest) => this.userRequestApi.cancelRequest(userRequest);

    deleteUserRequest = (userRequest: CplUserRequest) => this.userRequestApi.deleteRequest(userRequest);

    duplicateUserRequest = (userRequestID: string) => this.userRequestApi.duplicateRequest(userRequestID);

    getUsersWhoHaveSubmittedRequestItem = (requestItemID: string) => this.userRequestApi.getUsersWhoHaveSubmittedRequestItem(requestItemID);

    updateFlexHoursForUserRequest = (userRequestId: string, details: UserTimeTrackingSubmission) => this.userRequestApi.updateFlexHoursForUserRequest(userRequestId, details);
}